import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { Form } from "../../../components/Form";
import { Input } from "../../../components/Input";
import { FormSection } from "../../../components/FormSection";
import { OfferType } from "@KassaroAB/shared/types/enums/OfferType";
import type { OfferPercentage } from "@KassaroAB/shared/types/offer";
import { OfferCommonFields } from "./OfferCommonFields";
import { OfferTargetFields } from "./OfferTargetFields";
import type { OfferTypeFormProps } from "./offerFormTypes";
import { Fields } from "./offerFormStyles";
import {
  buildOfferBase,
  emptyOfferTarget,
  getOfferCommonDefaults,
} from "./offerUtils";
import { useOfferFormCatalog } from "./useOfferFormCatalog";
import { useOfferFormLifecycle } from "./useOfferFormLifecycle";

export const PercentageForm = ({
  formId,
  offer,
  savedTick,
  onSubmit,
  onDirtyChange,
  onTypeChange,
}: OfferTypeFormProps) => {
  const { t } = useTranslation();
  const { productOptions, groupOptions } = useOfferFormCatalog();
  const percentageOffer =
    offer?.type === OfferType.PERCENTAGE ? (offer as OfferPercentage) : null;
  const isEditing = !!offer;

  const form = useForm({
    defaultValues: {
      ...getOfferCommonDefaults(
        percentageOffer ??
          (offer ? { ...offer, type: OfferType.PERCENTAGE } : undefined),
      ),
      type: OfferType.PERCENTAGE as OfferType,
      target:
        percentageOffer?.target ??
        (offer && "target" in offer ? offer.target : emptyOfferTarget()),
      percentage: percentageOffer?.percentage ?? 10,
    },
    onSubmit: ({ value }) => {
      onSubmit({
        ...buildOfferBase(value),
        type: OfferType.PERCENTAGE,
        target: value.target,
        percentage: Number(value.percentage),
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);
  useOfferFormLifecycle({ form, savedTick, onDirtyChange, isDirty });

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <OfferCommonFields
        form={form}
        isEditing={isEditing}
        onTypeChange={onTypeChange}
      />
      <FormSection title={t("offerForm.additionalSettings")}>
        <Fields>
          <form.Field name="target">
            {(field) => (
              <OfferTargetFields
                value={field.state.value}
                onChange={field.handleChange}
                products={productOptions}
                groups={groupOptions}
                productsLabel={t("offerForm.products")}
                groupsLabel={t("offerForm.productGroups")}
              />
            )}
          </form.Field>
          <form.Field name="percentage">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("offerForm.percentage")}</FormLabel>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? 0 : Number(e.target.value),
                    )
                  }
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </Form>
  );
};
